/**
 * Customers.
 *
 * Two audiences in one list: regulars a delivery is going to, and registered
 * businesses that need a tax invoice in their own name. The NTN column matters
 * for the second group - a registered buyer changes what is reported to the
 * authority.
 */

import { useState, useCallback } from 'react';
import { FiPlus, FiSearch, FiUsers, FiPhone } from 'react-icons/fi';

import { customerApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { PageHeader, Badge } from '../components/ui/Feedback';
import { Field, Input, Checkbox, Textarea } from '../components/ui/Field';
import { money, formatDate, formatMobile } from '../utils/format';

const emptyCustomer = {
    customerName: '', mobileNo: '', phoneNo: '', email: '',
    ntn: '', cnic: '', strn: '', isRegisteredForSalesTax: false,
    address: '', city: '', notes: ''
};

const CustomersPage = () => {
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null);

    const load = useCallback(() => customerApi.search(search), [search]);
    const { data: customers, loading, refetch } = useAsync(load, [search]);

    const { run: save, busy } = useAction(
        (form) => customerApi.create(form),
        { successMessage: 'Customer added.', onSuccess: () => { setEditing(null); refetch(); } }
    );

    const columns = [
        {
            key: 'Customer_Name',
            header: 'Customer',
            primary: true,
            render: (row) => (
                <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">
                        {row.Customer_Name}
                        {row.Is_Walk_In && <Badge tone="slate" className="ml-2">default</Badge>}
                    </p>
                    {row.Customer_Code && (
                        <p className="text-xs text-slate-500">{row.Customer_Code}</p>
                    )}
                </div>
            )
        },
        {
            key: 'Mobile_No',
            header: 'Contact',
            render: (row) => (
                row.Mobile_No
                    ? (
                        <span className="flex items-center gap-1.5 text-slate-600">
                            <FiPhone className="h-3.5 w-3.5" /> {formatMobile(row.Mobile_No)}
                        </span>
                    )
                    : <span className="text-slate-400">—</span>
            )
        },
        {
            key: 'NTN',
            header: 'NTN / CNIC',
            render: (row) => (
                <div>
                    <span className="tabular text-xs text-slate-600">
                        {row.NTN || row.CNIC || '—'}
                    </span>
                    {row.Is_Registered_For_Sales_Tax && (
                        <Badge tone="green" className="ml-2">registered</Badge>
                    )}
                </div>
            )
        },
        {
            key: 'Address',
            header: 'Address',
            // A delivery address is three lines of text and the reason a card
            // would be twice the height of every other one.
            mobileHidden: true,
            render: (row) => (
                <span className="block max-w-[16rem] truncate text-slate-600">
                    {row.Address || '—'}
                </span>
            )
        },
        { key: 'Total_Orders', header: 'Orders', align: 'right' },
        {
            key: 'Total_Spend',
            header: 'Spent',
            align: 'right',
            render: (row) => <span className="font-medium">{money(row.Total_Spend)}</span>
        }
    ];

    return (
        <div className="page">
            <PageHeader
                title="Customers"
                subtitle="Regulars, delivery addresses, and registered buyers"
                actions={(
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setEditing({ ...emptyCustomer })}
                    >
                        <FiPlus className="h-4 w-4" /> New customer
                    </button>
                )}
            />

            <div className="relative w-full sm:max-w-md">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                    className="input pl-9"
                    placeholder="Search by name, mobile or NTN"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </div>

            <DataTable
                className="min-h-0 flex-1"
                columns={columns}
                rows={customers || []}
                loading={loading}
                rowKey={(row) => row.Customer_ID}
                emptyTitle="No customers"
                emptyDescription="Walk-in sales do not need one; add a customer for deliveries or tax invoices."
                emptyIcon={FiUsers}
            />

            <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title="New customer"
                width="md"
                dismissible={!busy}
                footer={(
                    <>
                        <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => save(editing)}
                            disabled={busy || !editing?.customerName?.trim()}
                        >
                            {busy ? 'Saving…' : 'Save customer'}
                        </button>
                    </>
                )}
            >
                {editing && (
                    <div className="space-y-3">
                        <Field label="Name" required>
                            <Input
                                value={editing.customerName}
                                onChange={(e) => setEditing({ ...editing, customerName: e.target.value })}
                                data-autofocus
                            />
                        </Field>

                        <div className="grid gap-3 sm:grid-cols-2">
                            <Field label="Mobile">
                                <Input
                                    value={editing.mobileNo}
                                    onChange={(e) => setEditing({ ...editing, mobileNo: e.target.value })}
                                    placeholder="0300-1234567"
                                />
                            </Field>
                            <Field label="Email">
                                <Input
                                    type="email"
                                    value={editing.email}
                                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                                />
                            </Field>
                        </div>

                        <Field label="Address">
                            <Input
                                value={editing.address}
                                onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                            />
                        </Field>

                        {/* Only relevant for a business buyer, so it is grouped and
                            explained rather than sitting loose among the contact
                            details. */}
                        <div className="rounded-xl bg-slate-50 p-3">
                            <p className="mb-2 text-xs text-slate-500">
                                For a customer who needs the invoice in their business name. A
                                registered buyer is reported differently to the authority.
                            </p>

                            <div className="grid gap-3 sm:grid-cols-3">
                                <Field label="NTN">
                                    <Input
                                        value={editing.ntn}
                                        onChange={(e) => setEditing({ ...editing, ntn: e.target.value })}
                                    />
                                </Field>
                                <Field label="CNIC">
                                    <Input
                                        value={editing.cnic}
                                        onChange={(e) => setEditing({ ...editing, cnic: e.target.value })}
                                    />
                                </Field>
                                <Field label="STRN">
                                    <Input
                                        value={editing.strn}
                                        onChange={(e) => setEditing({ ...editing, strn: e.target.value })}
                                    />
                                </Field>
                            </div>

                            <Checkbox
                                className="mt-3"
                                label="Registered for sales tax"
                                description="Reported as a registered buyer on the fiscal invoice."
                                checked={editing.isRegisteredForSalesTax}
                                onChange={(v) => setEditing({ ...editing, isRegisteredForSalesTax: v })}
                            />
                        </div>

                        <Field label="Notes">
                            <Textarea
                                rows={2}
                                value={editing.notes}
                                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                placeholder="Allergies, preferences, directions to the house…"
                            />
                        </Field>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default CustomersPage;
